import React, { useState, useEffect } from 'react';
import useAppStore from '../../store';

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// CSS-STYLES
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
const TableStyles = () => (
    <style>{`
        .pe-tooltip-wrapper { position: relative; }
        .pe-tooltip-wrapper:hover::after {
            content: attr(data-tooltip);
            position: absolute;
            bottom: 100%; left: 50%; transform: translateX(-50%);
            background-color: #E6C299; color: #000000;
            padding: 8px 12px; border-radius: 4px;
            font-size: 0.75rem; font-weight: 700;
            white-space: normal; min-width: 220px; max-width: 300px;
            z-index: 2147483647 !important; pointer-events: none;
            box-shadow: 0 10px 25px rgba(0,0,0,0.8);
            margin-bottom: 12px; text-align: center;
            border: 2px solid #ffffff; line-height: 1.3;
        }
        .pe-tooltip-wrapper:hover::before {
            content: ''; position: absolute;
            bottom: 100%; left: 50%; margin-left: -6px;
            border-width: 6px; border-style: solid;
            border-color: #E6C299 transparent transparent transparent;
            margin-bottom: 0px; z-index: 2147483647 !important;
        }
        
        /* Spaltenbreiten für Unternehmer */
        th.col-ent-date, td.col-ent-date { min-width: 170px !important; width: 170px !important; }
        th.col-ent-pos,  td.col-ent-pos  { min-width: 200px !important; }
        th.col-ent-money, td.col-ent-money { min-width: 180px !important; }
        th.col-ent-small, td.col-ent-small { min-width: 120px !important; }
        
        .status-badge-grey { color: #94a3b8; font-size: 0.7rem; letter-spacing: 0.05em; font-weight: 700; }
        .input-error { border: 2px solid #ef4444 !important; background-color: rgba(239, 68, 68, 0.1); }
        .ent-badge { background-color: rgba(230, 194, 153, 0.1); color: #E6C299; padding: 4px 12px; border-radius: 4px; border: 1px solid #E6C299; font-weight: 700; font-size: 0.85rem; display: inline-block; text-align: center; width: 100%; }
        
        /* GHOSTING LOGIC */
        .table-input.ghost-value { color: #64748b !important; } /* Grau für Altdaten */
        .table-input.active-value { color: #ffffff !important; font-weight: 800; } /* Weiß für Änderungen */

        /* ADMIN BADGE STYLE */
        .admin-badge { 
            background-color: rgba(6, 78, 59, 0.9); 
            border: 1px solid #10b981; 
            color: #d1fae5; 
            font-size: 0.65rem; 
            padding: 4px 2px; 
            border-radius: 4px; 
            display: flex; 
            flex-direction: column; 
            align-items: center; 
            justify-content: center;
            line-height: 1.1; 
            white-space: nowrap; 
            cursor: pointer; 
            transition: all 0.2s;
            width: 100%;
            height: 2.8rem;
        }
        .admin-badge:hover {
            background-color: #ef4444;
            border-color: #f87171;
            color: white;
        }
        .admin-badge:hover span { display: none; }
        .admin-badge:hover::after { content: 'UNLOCK'; font-weight: 800; }
    `}</style>
);

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// 1. AKTIVE ZEILE (Bearbeitbar)
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

interface ActiveRowProps {
    itemId: string;
    onAdminLock: (id: string, isLocked: boolean) => void;
}

const ActiveEntrepreneurRow: React.FC<ActiveRowProps> = ({ itemId, onAdminLock }) => {
    const { clientData, settings, updateEntrepreneurDraft, removeEntrepreneurDraft, commitDraftState } = useAppStore();
    
    const draft = clientData.drafts.entrepreneur?.find(i => i.id === itemId);
    const originalData = clientData.unternehmerData || {}; // Zugriff auf Originaldaten für Vergleich
    const currency = settings.country?.currency === 'CHF' ? 'CHF' : 'EUR';

    const fmt = (val: any, decimals: number = 2) => {
        if (val === '' || val === undefined) return '';
        return new Intl.NumberFormat('de-DE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(val);
    };
    
    const parse = (val: string) => parseFloat(val.toString().replace(/\./g, '').replace(',', '.')) || 0;

    const [localValues, setLocalValues] = useState<any>({});

    // UNDO SYNCHRONISATION
    useEffect(() => {
        if (draft) {
            setLocalValues({
                ...draft.inputs,
                entnahme: fmt(draft.inputs.entnahme),
                social: fmt(draft.inputs.social),
                umsatz: fmt(draft.inputs.umsatz),
                hours: fmt(draft.inputs.hours, 1),
                days: fmt(draft.inputs.days, 1),
            });
        }
    }, [draft]);

    if (!draft) return null; 

    // HANDLER
    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        e.target.select(); 
    };

    const handleBlur = (field: string) => {
        let val = localValues[field];
        commitDraftState();

        if (['entnahme', 'social', 'umsatz', 'hours', 'days'].includes(field)) {
            const num = parse(val);
            updateEntrepreneurDraft(itemId, field, num);
            const dec = (field === 'hours' || field === 'days') ? 1 : 2;
            setLocalValues((prev: any) => ({ ...prev, [field]: fmt(num, dec) }));
        } else {
            updateEntrepreneurDraft(itemId, field, val);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            (e.target as HTMLElement).blur(); 
        }
    };

    const handleChange = (field: string, val: string) => {
        setLocalValues((prev: any) => ({ ...prev, [field]: val }));
    };

    const handleSelectChange = (field: string, val: string) => {
        commitDraftState();
        handleChange(field, val);
        updateEntrepreneurDraft(itemId, field, val);
    };

    // VISUAL HELPER: Prüft ob Wert geändert wurde
    const getClass = (field: string, draftVal: any, origVal: number | undefined) => {
        const numVal = typeof draftVal === 'string' ? parse(draftVal) : draftVal;
        const isDiff = Math.abs((numVal || 0) - (origVal || 0)) > 0.01;
        
        // Error Check (nur wenn leer)
        if (['entnahme', 'social', 'hours'].includes(field)) {
             if (numVal === 0 && field !== 'social') return 'input-error'; // Social darf 0 sein, Entnahme/Stunden eher nicht
        }
        
        return isDiff ? 'active-value' : 'ghost-value';
    };

    // LOGIK
    const calcs = draft.calculations;
    const isLocked = draft.isLocked; // ADMIN STATUS
    const rowClass = `pe-row active-row ${isLocked ? 'locked-row' : ''}`;
    
    return (
        <tr className={rowClass}>
            {/* RESET - FIX: ROTER MÜLLEIMER */}
            <td className="col-tight" style={{ width: '1px' }}>
                <div className="pe-tooltip-wrapper" data-tooltip="Änderung verwerfen / Löschen">
                    <button className="reset-btn-row" onClick={() => removeEntrepreneurDraft(itemId)} disabled={isLocked}>
                        <i className="fas fa-trash-alt" style={{ color: isLocked ? '#475569' : '#ef4444', fontSize: '0.8rem' }}></i>
                    </button>
                </div>
            </td>

            {/* DATUM */}
            <td className="col-ent-date">
                <div className="pe-tooltip-wrapper" data-tooltip="Gültig ab (Monatserster)">
                    <select 
                        className="table-select field-date input-active-look" 
                        value={localValues.date} 
                        onChange={(e) => handleSelectChange('date', e.target.value)}
                        disabled={isLocked}
                    >
                        {Array.from({length: 12}, (_, i) => {
                            const d = `01.${String(i+1).padStart(2,'0')}.`;
                            return <option key={d} value={d}>{d}</option>
                        })}
                    </select>
                </div>
            </td>

            <td className="col-ent-pos">
                <div className="ent-badge">
                    <i className="fas fa-user-tie mr-2"></i>
                    Unternehmer
                </div>
            </td>

            {/* PRIVATENTNAHME */}
            <td className="col-ent-money">
                <div className="pe-tooltip-wrapper" data-tooltip={`Aktuell: ${fmt(originalData.entnahme)} ${currency}`}>
                    <div className="input-wrapper">
                        <input 
                            type="text"
                            className={`table-input ${getClass('entnahme', localValues.entnahme, originalData.entnahme)}`}
                            value={localValues.entnahme} 
                            onChange={(e)=>handleChange('entnahme', e.target.value)} 
                            onBlur={()=>handleBlur('entnahme')}
                            onFocus={handleFocus}
                            onKeyDown={handleKeyDown}
                            disabled={isLocked}
                        />
                        <div className="unit-label"><span className="unit-text">{currency}</span></div>
                    </div>
                </div>
            </td>

            {/* SOZIALKOSTEN */}
            <td className="col-ent-money">
                <div className="pe-tooltip-wrapper" data-tooltip={`Aktuell: ${fmt(originalData.sozialkosten)} ${currency}`}>
                    <div className="input-wrapper">
                        <input 
                            type="text"
                            className={`table-input ${getClass('social', localValues.social, originalData.sozialkosten)}`}
                            value={localValues.social} 
                            onChange={(e)=>handleChange('social', e.target.value)} 
                            onBlur={()=>handleBlur('social')}
                            onFocus={handleFocus}
                            onKeyDown={handleKeyDown}
                            disabled={isLocked}
                        />
                        <div className="unit-label"><span className="unit-text">{currency}</span></div>
                    </div>
                </div>
            </td>

            {/* GESAMTBELASTUNG (Calc) */}
            <td className="col-ent-money">
                <div className="pe-tooltip-wrapper" data-tooltip="Summe (Entnahme + Sozial)">
                    <div className="result-wrapper">
                        <div className="table-calc">{fmt(calcs.totalBelastung)}</div>
                        <div className="unit-label"><span className="unit-text">{currency}</span></div>
                    </div>
                </div>
            </td>

            {/* UMSATZ */}
            <td className="col-ent-money">
                <div className="pe-tooltip-wrapper" data-tooltip={`Aktuell: ${fmt(originalData.durchschnittsumsatz)} ${currency}`}>
                    <div className="input-wrapper">
                        <input 
                            type="text"
                            className={`table-input ${getClass('umsatz', localValues.umsatz, originalData.durchschnittsumsatz)}`}
                            value={localValues.umsatz} 
                            onChange={(e)=>handleChange('umsatz', e.target.value)} 
                            onBlur={()=>handleBlur('umsatz')}
                            onFocus={handleFocus}
                            onKeyDown={handleKeyDown}
                            disabled={isLocked}
                        />
                        <div className="unit-label"><span className="unit-text">{currency}</span></div>
                    </div>
                </div>
            </td>

            {/* STUNDEN */}
            <td className="col-ent-small">
                <div className="pe-tooltip-wrapper" data-tooltip={`Aktuell: ${fmt(originalData.prodWochenstunden, 1)} Std.`}>
                    <div className="input-wrapper">
                        <input 
                            type="text"
                            className={`table-input ${getClass('hours', localValues.hours, originalData.prodWochenstunden)}`}
                            value={localValues.hours} 
                            onChange={(e)=>handleChange('hours', e.target.value)} 
                            onBlur={()=>handleBlur('hours')}
                            onFocus={handleFocus}
                            onKeyDown={handleKeyDown}
                            disabled={isLocked}
                        />
                        <div className="unit-label"><span className="unit-text">Std.</span></div>
                    </div>
                </div>
            </td>

            {/* TAGE */}
            <td className="col-ent-small">
                <div className="pe-tooltip-wrapper" data-tooltip={`Aktuell: ${fmt(originalData.prodWochentage, 1)} T.`}>
                    <div className="input-wrapper">
                        <input 
                            type="text"
                            className={`table-input ${getClass('days', localValues.days, originalData.prodWochentage)}`}
                            value={localValues.days} 
                            onChange={(e)=>handleChange('days', e.target.value)} 
                            onBlur={()=>handleBlur('days')}
                            onFocus={handleFocus}
                            onKeyDown={handleKeyDown}
                            disabled={isLocked}
                        />
                        <div className="unit-label"><span className="unit-text">T.</span></div>
                    </div>
                </div>
            </td>

            <td className="col-expand">
                <div className="pe-tooltip-wrapper" data-tooltip="Notizen...">
                    <textarea 
                        className="table-textarea" 
                        value={localValues.notes} 
                        onChange={(e)=>handleChange('notes', e.target.value)} 
                        onBlur={()=>handleBlur('notes')} 
                        placeholder="Notiz..."
                        disabled={isLocked}
                    ></textarea>
                </div>
            </td>
            
            {/* ADMIN SPERRE */}
            <td className="col-tight">
                {isLocked ? (
                    <div className="admin-badge" onClick={() => onAdminLock(itemId, true)}>
                        <span className="text-lg font-bold">{draft.lockedBy}</span>
                        <span style={{ fontSize: '0.55rem' }}>{draft.lockedAt}</span>
                    </div>
                ) : (
                    <button className="btn-admin-lock" onClick={() => onAdminLock(itemId, false)}>
                        <i className="fas fa-lock"></i>
                    </button>
                )}
            </td>
        </tr>
    );
};

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// 2. JOKER ZEILE (Neuer Eintrag)
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

const JokerEntrepreneurRow: React.FC = () => {
    const { addEntrepreneurDraft, commitDraftState, updateEntrepreneurDraft, clientData, settings } = useAppStore();
    const [selectedDate, setSelectedDate] = useState('');
    const originalData = clientData.unternehmerData || {};
    const currency = settings.country?.currency === 'CHF' ? 'CHF' : 'EUR';

    const fmt = (val: number | undefined, dec = 2) => val ? new Intl.NumberFormat('de-DE', { minimumFractionDigits: dec, maximumFractionDigits: dec }).format(val) : (dec === 1 ? '0,0' : '0,00');

    const handleDateSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const date = e.target.value;
        setSelectedDate(date);
        
        if (date) {
            commitDraftState();
            addEntrepreneurDraft();
            
            setTimeout(() => {
                const drafts = useAppStore.getState().clientData.drafts.entrepreneur;
                if (drafts && drafts.length > 0) {
                    const lastItem = drafts[drafts.length - 1];
                    updateEntrepreneurDraft(lastItem.id, 'date', date);
                    
                    // VORBELEGUNG MIT IST-WERTEN
                    if (originalData) {
                        if (originalData.entnahme) updateEntrepreneurDraft(lastItem.id, 'entnahme', originalData.entnahme);
                        if (originalData.sozialkosten) updateEntrepreneurDraft(lastItem.id, 'social', originalData.sozialkosten);
                        if (originalData.durchschnittsumsatz) updateEntrepreneurDraft(lastItem.id, 'umsatz', originalData.durchschnittsumsatz);
                        if (originalData.prodWochenstunden) updateEntrepreneurDraft(lastItem.id, 'hours', originalData.prodWochenstunden);
                        if (originalData.prodWochentage) updateEntrepreneurDraft(lastItem.id, 'days', originalData.prodWochentage);
                    }
                }
            }, 0);
            
            setSelectedDate('');
        }
    };

    return (
        <tr className="pe-row">
            {/* RESET - PLUS SYMBOL FÜR NEUE ZEILE */}
            <td className="col-tight"><button className="reset-btn-row" disabled><i className="fas fa-plus text-stone-300 dark:text-slate-700"></i></button></td>

            {/* DATUM - DER TRIGGER */}
            <td className="col-ent-date">
                <div className="pe-tooltip-wrapper" data-tooltip="Datum wählen für Änderung">
                    <select 
                        className={`table-select field-date ${!selectedDate ? 'border-blue-500/50' : ''}`} 
                        value={selectedDate} 
                        onChange={handleDateSelect}
                    >
                        <option value="">Startdatum wählen</option>
                        {Array.from({length: 12}, (_, i) => {
                            const d = `01.${String(i+1).padStart(2,'0')}.`;
                            return <option key={d} value={d}>{d}</option>
                        })}
                    </select>
                </div>
            </td>

            {/* GHOST VALUES */}
            <td className="col-ent-pos">
                <div className="ent-badge opacity-50"><i className="fas fa-user-tie mr-2"></i>Unternehmer</div>
            </td>
            <td className="col-ent-money">
                <div className="input-wrapper">
                    <input className="table-input ghost-value" disabled placeholder={fmt(originalData.entnahme)} />
                    <div className="unit-label"><span className="unit-text">{currency}</span></div>
                </div>
            </td>
            <td className="col-ent-money">
                <div className="input-wrapper">
                    <input className="table-input ghost-value" disabled placeholder={fmt(originalData.sozialkosten)} />
                    <div className="unit-label"><span className="unit-text">{currency}</span></div>
                </div>
            </td>
            <td className="col-ent-money">
                <div className="result-wrapper">
                    <div className="table-calc opacity-50">{fmt((originalData.entnahme||0)+(originalData.sozialkosten||0))}</div>
                    <div className="unit-label"><span className="unit-text">{currency}</span></div>
                </div>
            </td>
            <td className="col-ent-money">
                <div className="input-wrapper">
                    <input className="table-input ghost-value" disabled placeholder={fmt(originalData.durchschnittsumsatz)} />
                    <div className="unit-label"><span className="unit-text">{currency}</span></div>
                </div>
            </td>
            <td className="col-ent-small">
                <div className="input-wrapper">
                    <input className="table-input ghost-value" disabled placeholder={fmt(originalData.prodWochenstunden, 1)} />
                    <div className="unit-label"><span className="unit-text">Std.</span></div>
                </div>
            </td>
            <td className="col-ent-small">
                <div className="input-wrapper">
                    <input className="table-input ghost-value" disabled placeholder={fmt(originalData.prodWochentage, 1)} />
                    <div className="unit-label"><span className="unit-text">T.</span></div>
                </div>
            </td>
            <td className="col-expand"><textarea className="table-textarea" disabled placeholder="Notizen..."></textarea></td>
            <td><button className="btn-admin-lock" disabled><i className="fas fa-lock"></i></button></td>
        </tr>
    );
};

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// HAUPT-KOMPONENTE
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

interface EntrepreneurTableProps {
    onAdminLock: (id: string, isLocked: boolean) => void;
}

const EntrepreneurTable: React.FC<EntrepreneurTableProps> = ({ onAdminLock }) => {
    const { clientData } = useAppStore();
    const entrepreneurDrafts = clientData.drafts.entrepreneur || [];

    return (
        <div className="table-wrapper">
            <TableStyles />
            <table className="text-left border-collapse">
                <thead className="pe-table-header">
                    <tr>
                        <th className="col-tight px-2 min-w-[50px]">Reset</th>
                        <th className="col-ent-date px-2">Gültig ab</th>
                        <th className="col-ent-pos px-2">Position</th>
                        <th className="col-ent-money px-2">Privatentnahme</th>
                        <th className="col-ent-money px-2">Sozialkosten</th>
                        <th className="col-ent-money px-2">Σ Entnahme</th>
                        <th className="col-ent-money px-2">Umsatz (Netto/Plan)</th>
                        <th className="col-ent-small px-2">Std./Wo</th>
                        <th className="col-ent-small px-2">Tage/Wo</th>
                        <th className="col-expand px-2 min-w-[400px]">Info / Notizen</th>
                        <th className="col-tight px-2 min-w-[80px]">Admin</th>
                    </tr>
                </thead>
                <tbody id="entrepreneurBody">
                    {/* 1. AKTIVE DRAFTS */}
                    {entrepreneurDrafts.map(item => (
                        <ActiveEntrepreneurRow key={item.id} itemId={item.id} onAdminLock={onAdminLock} />
                    ))}

                    {/* 2. EINGABE-ZEILE (JOKER) */}
                    <JokerEntrepreneurRow />
                </tbody>
            </table>
        </div>
    );
};

export default EntrepreneurTable;